/**
 * **The round-boundary read** — when "the end of round `roundLimit`" has occurred, for the
 * four zone-scoring sitreps (Control/Holdout/Recon at 6 rounds, Gauntlet at 8) that decide
 * "at the end of round N" (Core pp. 267–273). Shared by every zone-scoring predicate module
 * (`sitrep/predicates/*`) so the boundary formula lives once rather than drifting across four
 * copies; each predicate module still owns its own heterogeneous WINNER logic.
 *
 * Composes {@link roundComplete} (`packages/domain/src/encounter/activation.ts`) — the SAME
 * derived boolean the reducer's `end_round` legality gate reads.
 */
import { roundComplete, type EncounterState } from "@lancer-vtt/domain";

/**
 * Whether the sitrep's round cap has been reached — "the end of round `roundLimit`"
 * has occurred. Two ways to satisfy it:
 *   - `activation.round > roundLimit` — the round counter has already advanced past
 *     the cap (an `end_round` bumped it into the next round, or a GM `set_activation`
 *     skipped ahead). Unconditionally past the boundary regardless of what's
 *     mid-round right now.
 *   - `activation.round === roundLimit && roundComplete(state)` — still nominally
 *     IN the capped round, but every able unit on both sides has already acted and
 *     nobody is mid-turn: round `roundLimit`'s own activations are genuinely
 *     finished even though nobody has issued the separate `end_round` mutation yet.
 *     `sitrepStatus` is a pure read — it must not depend on whether the GM has
 *     gotten around to clicking End Round, so it reads the boundary a beat early via
 *     the same derived fact `end_round`'s own legality gate uses.
 *
 * `activation.round < roundLimit` is always before the boundary regardless of
 * `roundComplete` — an EARLIER round finishing early doesn't end the sitrep early;
 * only the capped round's own completion (or advancing past it) does.
 */
export function roundLimitReached(state: EncounterState, roundLimit: number): boolean {
  const round = state.activation.round;
  if (round > roundLimit) return true;
  return round === roundLimit && roundComplete(state);
}

/** `roundLimit − current round`, floored at 0 — the countdown every round-capped sitrep type surfaces. */
export function roundsRemainingFor(state: EncounterState, roundLimit: number): number {
  return Math.max(0, roundLimit - state.activation.round);
}
